import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input, output, TemplateRef } from "@angular/core";
import { UserStore } from "../../../stores/user.store";
import { AcademicCourseLabel, academicLeafDetail } from "../../academic-curriculum/academic.progress.labels";
import { ProgressNode } from "./trail.api";
import { TrailLabels } from "./trail.labels";
import { progressGroupName, progressNodeTitle, progressPercent, progressStatusLabel } from "./trail.progress.display";
import { formatUserprefDate } from "./trail.userpref.date";

@Component({
    selector: "s25-ng-trail-progress-tree",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule],
    host: { class: "trail-progress-tree" },
    template: `
        <div class="trail-progress-tree__row" [attr.data-id]="nodeDataId()">
            <div class="trail-progress-tree__copy">
                <span
                    class="trail-progress-tree__title"
                    [class.trail-progress-tree__title--group]="node().nodeType === 'group'"
                    data-id="progress-node-title"
                >
                    {{ title() }}
                </span>
                @if (groupName()) {
                    <span class="trail-progress-tree__group-name" data-id="progress-node-group-name">{{
                        groupName()
                    }}</span>
                }
            </div>
            <span
                class="trail-progress-tree__status trail-progress-tree__status--{{ node().status }}"
                data-id="progress-node-status"
            >
                {{ statusText() }}
            </span>
            @if (node().threshold && node().threshold > 0) {
                <div class="trail-progress-tree__progress-wrap" data-id="progress-node-bar">
                    <div class="trail-progress-tree__progress">
                        <div class="trail-progress-tree__progress-fill" [style.width.%]="fillPercent()"></div>
                    </div>
                    <span class="trail-progress-tree__progress-text"
                        >{{ node().current ?? 0 }}/{{ node().threshold }}</span
                    >
                </div>
            }
            @if (node().deadlineStartDt || node().deadlineEndDt) {
                <span class="trail-progress-tree__deadline">
                    @if (node().deadlineStartDt) {
                        {{ formatDateTime(node().deadlineStartDt, "") }}
                    }
                    @if (node().deadlineStartDt && node().deadlineEndDt) {
                        –
                    }
                    @if (node().deadlineEndDt) {
                        {{ formatDateTime(node().deadlineEndDt, "") }}
                    }
                </span>
            }
            @if (showSubmitExperience() && node().experienceSubmit?.canSubmit) {
                <button
                    type="button"
                    class="trail-progress-tree__submit"
                    data-id="submit-experience"
                    (click)="onSubmitExperience()"
                >
                    {{ labels.SUBMIT_EXPERIENCE }}
                </button>
            }
            @if (node().override?.overridden) {
                <span class="trail-progress-tree__waived">{{ labels.WAIVED }}</span>
            }
            @if (node().experienceSubmit?.pendingExperienceId) {
                <span class="trail-progress-tree__pending">{{ labels.APPROVAL_PENDING }}</span>
            }
            @if (node().experienceSubmit?.denialReason) {
                <span class="trail-progress-tree__denied"
                    >{{ labels.DENIED }}: {{ node().experienceSubmit.denialReason }}</span
                >
            }
            @if (actionsTemplate(); as actions) {
                <ng-container *ngTemplateOutlet="actions; context: { $implicit: node() }" />
            }
        </div>
        @if (children().length) {
            <ul class="trail-progress-tree__children">
                @for (child of children(); track child.nodeId ?? $index) {
                    <li class="trail-progress-tree__child">
                        <s25-ng-trail-progress-tree
                            [node]="child"
                            [courses]="courses()"
                            [showSubmitExperience]="showSubmitExperience()"
                            [actionsTemplate]="actionsTemplate()"
                            (submitExperience)="submitExperience.emit($event)"
                        />
                    </li>
                }
            </ul>
        }
    `,
    styles: [
        `
            :host {
                display: block;
            }
            .trail-progress-tree__row {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                flex-wrap: wrap;
                min-height: 1.7rem;
            }
            .trail-progress-tree__copy {
                display: flex;
                flex-direction: column;
                gap: 0.1rem;
                min-width: 0;
            }
            .trail-progress-tree__title {
                font-size: 0.9rem;
                font-weight: 600;
                color: var(--color--text);
            }
            .trail-progress-tree__title--group {
                font-weight: 500;
                color: var(--color--text);
            }
            .trail-progress-tree__group-name {
                font-size: 0.78rem;
                color: var(--color--text-muted);
            }
            .trail-progress-tree__status {
                padding: 0.15rem 0.45rem;
                border-radius: 4px;
                font-size: 0.75rem;
                font-weight: 600;
            }
            .trail-progress-tree__status--satisfied {
                background: #d4edda;
                color: #155724;
            }
            .trail-progress-tree__status--in_progress {
                background: #fff3cd;
                color: #856404;
            }
            .trail-progress-tree__status--not_started {
                background: #e2e3e5;
                color: #383d41;
            }
            .trail-progress-tree__status--not_yet_open {
                background: #d6d8db;
                color: #383d41;
            }
            .trail-progress-tree__status--expired {
                background: #f8d7da;
                color: #721c24;
            }
            .trail-progress-tree__status--locked {
                background: #d6d8db;
                color: #383d41;
            }
            .trail-progress-tree__progress-wrap {
                display: flex;
                align-items: center;
                gap: 0.35rem;
            }
            .trail-progress-tree__progress {
                width: 100px;
                height: 8px;
                background: var(--color--border);
                border-radius: 4px;
                overflow: hidden;
            }
            .trail-progress-tree__progress-fill {
                height: 100%;
                background: var(--cn-navy, #1e3a5f);
                border-radius: 4px;
                transition: width 0.3s ease;
            }
            .trail-progress-tree__progress-text {
                font-size: 0.75rem;
                font-weight: 600;
                color: var(--color--text-muted, #555);
            }
            .trail-progress-tree__deadline {
                font-size: 0.78rem;
                color: var(--color--text-muted);
            }
            .trail-progress-tree__submit {
                padding: 0.25rem 0.6rem;
                border: 1px solid var(--cn-navy, #1e3a5f);
                border-radius: 4px;
                background: transparent;
                color: var(--cn-navy, #1e3a5f);
                font-size: 0.78rem;
                cursor: pointer;
                font-weight: 500;
            }
            .trail-progress-tree__submit:hover {
                background: var(--cn-navy, #1e3a5f);
                color: #fff;
            }
            .trail-progress-tree__pending,
            .trail-progress-tree__waived {
                font-size: 0.78rem;
                color: var(--color--text-muted);
                font-style: italic;
            }
            .trail-progress-tree__denied {
                font-size: 0.78rem;
                color: var(--color--danger);
            }
            .trail-progress-tree__children {
                list-style: none;
                margin: 0.15rem 0 0;
                padding: 0;
            }
            .trail-progress-tree__child {
                position: relative;
                padding-left: 1.35rem;
            }
            .trail-progress-tree__child::before {
                content: "";
                position: absolute;
                left: 0;
                top: 0;
                width: 1.05rem;
                height: 0.95rem;
                border-left: 2px solid var(--color--border);
                border-bottom: 2px solid var(--color--border);
                border-bottom-left-radius: 0.7rem;
            }
            .trail-progress-tree__child:not(:last-child)::after {
                content: "";
                position: absolute;
                left: 0;
                top: 0.95rem;
                bottom: 0;
                border-left: 2px solid var(--color--border);
            }
        `,
    ],
})
export class TrailProgressTreeComponent {
    private readonly userStore = inject(UserStore);

    readonly node = input.required<ProgressNode>();
    readonly courses = input<AcademicCourseLabel[]>([]);
    readonly showSubmitExperience = input(false);
    readonly actionsTemplate = input<TemplateRef<{ $implicit: ProgressNode }> | undefined>(undefined);
    readonly submitExperience = output<ProgressNode>();
    readonly labels = TrailLabels;

    readonly title = computed(() => {
        const node = this.node();
        if (node.nodeType === "group") return progressNodeTitle(node);
        return academicLeafDetail(node, this.courses()) ?? progressNodeTitle(node);
    });
    readonly groupName = computed(() =>
        this.node().nodeType === "group" ? progressGroupName(this.node()) : undefined,
    );
    readonly statusText = computed(() => progressStatusLabel(this.node().status));
    readonly fillPercent = computed(() => progressPercent(this.node()));
    readonly children = computed(() => this.node().children ?? []);
    readonly nodeDataId = computed(() => `progress-node-${this.node().nodeId ?? "root"}`);

    formatDateTime(value: string | undefined | null, empty = "—"): string {
        return formatUserprefDate(value, this.userStore.dateTimeFormat(), empty);
    }

    onSubmitExperience(): void {
        this.submitExperience.emit(this.node());
    }
}
